import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileClock, Plus, Trash2, Users, LogIn, Mail, MapPinned, Clock, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useStats } from "@/context/StatsContext";
import { Panel, Empty, ResBadge, DepositBadge, CardSentBadge, CompletionBadge, ExpiredLinkFlag, RowSearch, useQueryFilter, matchesQuery } from "@/pages/shared";
import RowActions from "@/components/RowActions";
import { setCurrentDraft, clearCurrentDraft } from "@/lib/draft";
import EditReservationDialog from "@/components/EditReservationDialog";
import CheckinDialog from "@/components/CheckinDialog";
import ReservationForm, { emptyReservation, stayConflict, missingDefinitive, provisionalOk } from "@/components/ReservationForm";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function Reservaties() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const openedRef = React.useRef(null);
  const { reload } = useStats();
  const [rows, setRows] = useState([]);
  const [guests, setGuests] = useState([]);
  const [zones, setZones] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyReservation());
  const [q, setQ] = useQueryFilter();
  const [editing, setEditing] = useState(null);
  const [checkin, setCheckin] = useState(null);

  const load = async () => {
    try {
      const [r, g, z, p] = await Promise.all([api.get("/reservations?filter=with_pitch"), api.get("/guests"), api.get("/zones"), api.get("/pitches")]);
      setRows(r.data); setGuests(g.data); setZones(z.data); setPitches(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Vanuit de plattegrond: ?pitch=A12 opent direct een nieuwe reservatie voor die plaats
  useEffect(() => {
    const label = params.get("pitch");
    if (!label || !pitches.length || openedRef.current === `p:${label}`) return;
    const p = pitches.find((x) => x.label === label);
    if (!p) return;
    openedRef.current = `p:${label}`;
    setForm({ ...emptyReservation(), pitch_label: p.label, zone: p.zone });
    setOpen(true);
  }, [params, pitches]);

  // Vanuit een link (plattegrond/mailbox): ?id= opent meteen de incheck-dialoog
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || openedRef.current === `id:${id}`) return;
    const r = rows.find((x) => x.id === id);
    if (r) { openedRef.current = `id:${id}`; setCheckin(r); }
  }, [params, rows]);

  // Vanuit de mailbox: ?from_mail=1 met gegevens van de afzender in sessionStorage
  useEffect(() => {
    if (!params.get("from_mail") || openedRef.current === "mail") return;
    const raw = sessionStorage.getItem("campad.mail_prefill");
    if (!raw) return;
    const d = JSON.parse(raw);
    if (d.guest_id && !guests.length) return;
    openedRef.current = "mail";
    sessionStorage.removeItem("campad.mail_prefill");
    const g = guests.find((x) => x.id === d.guest_id) || {};
    const base = emptyReservation(d.arrival || "");
    const fromGuest = Object.fromEntries(Object.entries(g).filter(([k]) => k in base.booker));
    const reqPitch = pitches.find((x) => x.label === (d.pitch_label || "").trim());
    setMovePlan(Array.isArray(d.move_plan) && d.move_plan.length > 1 ? d.move_plan : null);
    setForm({ ...base, guest_id: g.id || "", departure: d.departure || "", notes: d.note || "",
      pitch_label: reqPitch?.label || "", zone: reqPitch?.zone || "",
      booker: { ...base.booker, first_name: d.first_name, last_name: d.last_name, email: d.email, phone: d.phone || "", passport_number: d.passport_number || "", ...fromGuest, language: g.language || d.language || "nl" },
      stay: { ...base.stay, adults: Math.max(1, d.adults || base.stay.adults), children: d.children || 0, dogs: d.dogs || 0 } });
    setOpen(true);
    setFromMail(true);
    toast.info(t("mb_prefilled"));
  }, [params, guests]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showMissing, setShowMissing] = useState(false);
  const [fromMail, setFromMail] = useState(false);
  const [movePlan, setMovePlan] = useState(null);

  useEffect(() => {
    if (open) setCurrentDraft({ kind: "reservation", data: form }); else clearCurrentDraft();
    return clearCurrentDraft;
  }, [open, form]);
  const [drafts, setDrafts] = useState({ own: null, others: [], notice: null });
  const loadDrafts = () => api.get("/me/draft").then(({ data }) => setDrafts(data)).catch(() => {});
  useEffect(() => { loadDrafts(); }, []);
  const discardDraft = async () => { await api.delete("/me/draft").catch(() => {}); loadDrafts(); };
  const resumeDraft = async (d) => {
    try {
      const { data } = await api.post(`/me/draft/${d.user_id}/take`);
      setForm({ ...emptyReservation(), ...data.data }); setOpen(true); toast.success(t("draft_resumed"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    loadDrafts();
  };
  const dismissNotice = async () => { await api.delete("/me/draft/notice").catch(() => {}); loadDrafts(); };
  const draftList = [...(drafts.own ? [{ ...drafts.own, mine: true }] : []), ...drafts.others];
  const visible = rows.filter((r) => matchesQuery(r, q));
  const conflict = stayConflict(form.stay.items);
  const canProvisional = provisionalOk(form) && !conflict;

  const printRegistration = (r) => window.open(`/print?res=${r.id}&lang=${r.language || "nl"}&map=1`, "_blank", "noopener");

  const save = async (definitive) => {
    if (definitive && missingDefinitive(form).length) { setShowMissing(true); toast.error(t("missing_fields")); return; }
    try {
      if (movePlan && movePlan.length > 1) {
        const { data } = await api.post("/reservations/plan", {
          guest_id: form.guest_id || null, booker: form.booker, stay: form.stay, occupants: form.occupants,
          notes: form.notes, definitive, document_ids: form.document_ids || [], status: "reserved",
          segments: movePlan.map((s) => ({ pitch_label: s.pitch_label, zone: s.zone || "", arrival: s.arrival, departure: s.departure })),
        });
        setOpen(false); setForm(emptyReservation()); setShowMissing(false); setFromMail(false); setMovePlan(null);
        await Promise.all([load(), reload()]);
        toast.success(`${t("created")} · ${data.moves}× ${t("move_plan_moves")}`);
        return;
      }
      const { data } = await api.post("/reservations", { ...form, definitive, confirm_card: fromMail, meter_start: form.meter_start === "" || form.meter_start == null ? null : Number(form.meter_start) });
      setOpen(false);
      setForm(emptyReservation());
      setShowMissing(false);
      setFromMail(false);
      await Promise.all([load(), reload()]);
      toast.success(data.mail_sent ? `${t("created")} · ${t("mail_sent")}` : t("created"));
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.msg || apiError(d)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/reservations/${id}`); await Promise.all([load(), reload()]); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resendDeposit = async (res) => {
    try {
      const { data } = await api.post(`/reservations/${res.id}/resend-payment-request`);
      toast.success(`${t("deposit_resend_sent")} ${data.to}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={`${t("reservations")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="reservations-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("search_placeholder")} testid="reservations-search" />
          <Button onClick={() => setOpen(true)} data-testid="add-reservation-button"><Plus className="h-4 w-4 mr-1" />{t("new_reservation")}</Button>
        </div>
      }
    >
      {drafts.notice && (
        <div className="m-5 mb-0 flex flex-wrap items-center gap-3 rounded-xl border border-sky-300 bg-sky-50 px-4 py-3 text-sm dark:bg-sky-950/30" data-testid="draft-notice">
          <Users className="h-5 w-5 text-sky-700 shrink-0" />
          <p className="min-w-0 flex-1 text-sky-900 dark:text-sky-200">
            <span className="font-semibold">{t("draft_taken_title")}</span> — {t("draft_taken_text")} <strong>{drafts.notice.taken_by}</strong> ({new Date(drafts.notice.at).toLocaleString()})
            {drafts.notice.pitch_label ? ` · ${t("pitch")} ${drafts.notice.pitch_label}` : ""}{drafts.notice.booker ? ` · ${drafts.notice.booker}` : ""}
          </p>
          <Button size="sm" variant="ghost" onClick={dismissNotice} data-testid="draft-notice-dismiss">{t("close")}</Button>
        </div>
      )}
      {draftList.map((d) => (
        <div key={d.user_id} className="m-5 mb-0 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:bg-amber-950/30" data-testid={d.mine ? "draft-banner" : `draft-banner-${d.user_id}`}>
          <FileClock className="h-5 w-5 text-amber-700 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-amber-900 dark:text-amber-200">{d.mine ? t("draft_found") : `${t("draft_found_other")} ${d.user_name}`}</p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80">{t("draft_saved_at")} {new Date(d.saved_at).toLocaleString()}{d.data?.pitch_label ? ` · ${t("pitch")} ${d.data.pitch_label}` : ""}{d.data?.booker?.last_name ? ` · ${d.data.booker.first_name || ""} ${d.data.booker.last_name}` : ""}</p>
          </div>
          <Button size="sm" onClick={() => resumeDraft(d)} data-testid={d.mine ? "draft-resume" : `draft-take-${d.user_id}`}>{d.mine ? t("draft_resume") : t("draft_take")}</Button>
          {d.mine && <Button size="sm" variant="ghost" onClick={discardDraft} data-testid="draft-discard"><Trash2 className="h-4 w-4 mr-1" />{t("draft_discard")}</Button>}
        </div>
      ))}
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("arrival")}</TableHead>
              <TableHead>{t("departure")}</TableHead>
              <TableHead>{t("persons")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("deposit")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => {
              const late = r.arrival < today();
              return (
              <TableRow key={r.id} data-testid={`reservation-row-${r.id}`}>
                <TableCell className="font-medium">{r.guest_name}</TableCell>
                <TableCell><span className="font-semibold text-primary">{r.pitch_label}</span></TableCell>
                <TableCell className={late ? "font-medium text-rose-600 dark:text-rose-400" : "text-muted-foreground"}>
                  <div className="flex items-center gap-2">
                    {fmtDate(r.arrival)}
                    {late && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" data-testid={`late-badge-${r.id}`}>{t("overdue_label")}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.departure)}</TableCell>
                <TableCell><div className="flex items-center gap-2">{r.persons}<CompletionBadge definitive={r.definitive} testid={`completion-${r.id}`} /><ExpiredLinkFlag reservation={r} compact testid={`link-expired-${r.id}`} /></div></TableCell>
                <TableCell><ResBadge status={r.status} /></TableCell>
                <TableCell><div className="flex flex-wrap items-center gap-1"><DepositBadge reservation={r} testid={`deposit-badge-${r.id}`} onResend={resendDeposit} /><CardSentBadge reservation={r} testid={`card-sent-badge-${r.id}`} /></div></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {r.definitive === false && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title={t("send_complete_link")} data-testid={`send-complete-link-${r.id}`}
                        onClick={() => api.post(`/reservations/${r.id}/send-complete-link`).then(({ data }) => toast.success(`${t("complete_link_sent")} ${data.to}`)).catch((e) => toast.error(apiError(e.response?.data?.detail)))}>
                        <Mail className="h-4 w-4" />
                      </Button>
                    )}
                    {r.definitive === false && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title={t("extend_complete_link")} data-testid={`extend-complete-link-${r.id}`}
                        onClick={() => api.post(`/reservations/${r.id}/extend-complete-link`).then(({ data }) => { toast.success(`${t("complete_link_extended")} ${data.to}`); load(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)))}>
                        <Clock className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={t("send_confirmation_card")} data-testid={`send-confirmation-card-${r.id}`}
                      onClick={() => api.post(`/reservations/${r.id}/send-confirmation-card`).then(({ data }) => toast.success(`${t("confirmation_card_sent")} ${data.to}`)).catch((e) => toast.error(apiError(e.response?.data?.detail)))}>
                      <MapPinned className="h-4 w-4" />
                    </Button>
                    <RowActions r={r} withMap onEdit={setEditing} onDelete={(x) => remove(x.id)} />
                    <Button variant="outline" size="icon" className="h-8 w-8" title={t("print_registration")} data-testid={`print-reg-${r.id}`}
                      onClick={() => printRegistration(r)}>
                      <UserCheck className="h-4 w-4" />
                    </Button>
                    {r.status === "reserved" && (
                      <Button size="sm" onClick={() => setCheckin(r)} data-testid={`checkin-${r.id}`}>
                        <LogIn className="h-4 w-4 mr-1" />{t("checkin")}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ); })}
          </TableBody>
        </Table>
      )}

      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} onSaved={async () => { await Promise.all([load(), reload()]); }} guests={guests} zones={zones} pitches={pitches} />
      <CheckinDialog reservation={checkin} guests={guests} zones={zones} pitches={pitches} onClose={() => setCheckin(null)} onDone={async () => { await Promise.all([load(), reload()]); }} />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFromMail(false); setMovePlan(null); } }}>
        <DialogContent className="max-w-[96vw] xl:max-w-7xl max-h-[94vh] overflow-y-auto cb-scroll" data-testid="reservation-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("new_reservation")}</DialogTitle></DialogHeader>
          {movePlan && movePlan.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="reservation-move-plan">
              <span className="font-semibold">{t("move_plan_title")}:</span>
              <span className="flex flex-wrap items-center gap-1.5">
                {movePlan.map((s, i) => (
                  <span key={s.pitch_label} className="flex items-center gap-1.5">
                    {i > 0 && <span className="text-[11px] text-amber-700">→ {fmtDate(s.arrival)}</span>}
                    <span className="font-bold">{s.pitch_label}</span>
                  </span>
                ))}
              </span>
              <button type="button" onClick={() => setMovePlan(null)} className="ml-auto text-xs font-semibold text-amber-800 underline hover:no-underline" data-testid="reservation-move-plan-clear">{t("move_plan_single")}</button>
            </div>
          )}
          <ReservationForm form={form} setForm={setForm} guests={guests} zones={zones} pitches={pitches} highlightMissing={showMissing} />
          <DialogFooter className="sm:justify-between items-center gap-3">
            <p className="text-[11px] text-muted-foreground max-w-md">{t("provisional_hint")} {t("definitive_hint")}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button variant="secondary" onClick={() => save(false)} disabled={!canProvisional} data-testid="save-provisional-button">{t("save_provisional")}</Button>
              <Button onClick={() => save(true)} disabled={!canProvisional} data-testid="save-reservation-button">{t("save_definitive")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
